import React, { useEffect, useMemo, useState } from 'react';

import { List } from 'immutable';
import Divider from 'flow/components/ui-components/Divider';
import useAppContext from 'flow/hooks/useAppContext';
import { fetchQueuesWithPromise } from 'flow/actions/pbx-actions';
import {
    wallboardTypes,
    getCustomFieldsForHuntGroupsMultiple,
} from 'flow/utils/wallboard-utils';
import Queue from 'flow/types/Queue';
import { QueueKey } from 'flow/types/EntityKeys';

import { TypedMap } from 'flow/types/TypedMap';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import AddQueuesModal from './creation/hunt-groups/AddQueuesModal';
import Button from 'flow/components/ui-components/Button';
import DashboardSettings from 'flow/components/dashboard/DashboardSettings';
import DashboardSectionHeader from 'flow/components/dashboard/DashboardSectionHeader';
import QueuesSortableDeletable from 'flow/components/dashboard/creation/hunt-groups/QueuesSortableDeletable';

const getOmniQueueIsSelected = (
    huntGroups: TypedMap<Record<QueueKey, TypedMap<Queue>>>,
    huntGroupsSelectedKeys: List<QueueKey>,
) => {
    let omniQueueIsSelected = false;
    huntGroupsSelectedKeys?.forEach((key) => {
        if (huntGroups?.getIn([key, 'DTOSubtype']) === 'OmnichannelQueueDTO') {
            omniQueueIsSelected = true;
        }
    });
    return omniQueueIsSelected;
};

interface Props {
    onWallboardDataChanged: Function;
    wallboardData: any;
    maxSelectionAmount: number;
    onNavigateToCustomizeSettings: () => void;
    onNavigateToVisibilitySettings: () => void;
}

const WallboardHuntGroupsMultiple = ({
    onWallboardDataChanged,
    wallboardData,
    maxSelectionAmount,
    onNavigateToCustomizeSettings,
    onNavigateToVisibilitySettings,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const huntGroups = useAppSelector((state) => state.pbx.get('queues'));

    const [queuesModalOpen, setQueuesModalOpen] = useState(false);

    const serviceLevelPeriod = wallboardData.getIn([
        'settings',
        'serviceLevelPeriod',
    ]);
    const huntGroupsSelectedKeys: List<QueueKey> = wallboardData
        ? wallboardData.get('queueKeys')
        : List();

    useEffect(() => {
        if (!huntGroups || huntGroups.isEmpty()) {
            dispatch(fetchQueuesWithPromise(null));
        }
    }, [dispatch, huntGroups]);

    const useOmniCompatibleWording = useMemo(() => {
        return getOmniQueueIsSelected(huntGroups, huntGroupsSelectedKeys);
    }, [huntGroups, huntGroupsSelectedKeys]);

    const customFields = useMemo(() => {
        return getCustomFieldsForHuntGroupsMultiple(
            i18next,
            useOmniCompatibleWording,
            serviceLevelPeriod,
        );
    }, [i18next, useOmniCompatibleWording, serviceLevelPeriod]);

    const wallboardDescriptionChanged = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const description = event.target.value;
        const wallboardDataUpdated = wallboardData.set('name', description);

        onWallboardDataChanged(wallboardDataUpdated);
    };

    const onQueuesSortedOrDeleted = (queues: List<QueueKey>) => {
        onWallboardDataChanged(wallboardData.set('queueKeys', queues));
    };

    const onQueuesAdded = (queuesAdded: List<QueueKey>) => {
        const existingQueues = wallboardData.get('queueKeys') || List();
        const queuesUpdated = existingQueues.concat(queuesAdded).toList();
        onWallboardDataChanged(wallboardData.set('queueKeys', queuesUpdated));
    };

    const renderDataPointsDescriptions = () => {
        return (
            <>
                <div className="mt-12 mb-1.5 px-3 text-xs font-bold">
                    {i18next.t('wallboard.custom_fields.title')}
                </div>
                <Divider className="mt-1 mb-5" />
                <div className="flex flex-col px-3">
                    {customFields.map(
                        (field: TypedMap<CustomField>, index: number) => (
                            <div key={index}>
                                <span className="mb-2 block text-sm text-gray-800">
                                    {field.get('value')}
                                </span>
                                <span className="mb-5 block text-xs text-gray-500">
                                    {field.get('description')}
                                </span>
                            </div>
                        ),
                    )}
                </div>
            </>
        );
    };

    return (
        <React.Fragment>
            <div className="my-9">
                <DashboardSectionHeader
                    boardType={wallboardTypes.HUNT_GROUPS_MULTIPLE}
                    userDescription={wallboardData.get('name') || ''}
                    onDescriptionChanged={wallboardDescriptionChanged}
                    setFocusOnNameField={true}
                />
            </div>
            <div className="mt-3 mb-1.5 px-3 text-xs font-bold">
                {i18next.t('wallboard.hunt_groups_display')}
            </div>
            <Divider className="mt-1 mb-5" />
            <div className="px-3">
                <QueuesSortableDeletable
                    queues={wallboardData.get('queueKeys')}
                    onUpdate={onQueuesSortedOrDeleted}
                />
            </div>
            <Divider className="mt-5 mb-3" />
            <div className="ps-3">
                <Button
                    onClick={() => setQueuesModalOpen(true)}
                    color="primary"
                    iconKey="add"
                    className="py-1"
                >
                    {i18next.t('Add queues')}
                </Button>
            </div>
            {renderDataPointsDescriptions()}
            <Divider className="mt-1 mb-9" />
            <div className="mb-9">
                <DashboardSettings
                    wallboardSettings={wallboardData.get('settings')}
                    onNavigateToCustomizeSettings={
                        onNavigateToCustomizeSettings
                    }
                    onNavigateToVisibilitySettings={
                        onNavigateToVisibilitySettings
                    }
                    boardType={wallboardTypes.HUNT_GROUPS_MULTIPLE}
                />
            </div>
            {queuesModalOpen ? (
                <AddQueuesModal
                    queuesToExclude={wallboardData.get('queueKeys')}
                    onClickAdd={onQueuesAdded}
                    onClose={() => setQueuesModalOpen(false)}
                    maximumAmount={
                        maxSelectionAmount -
                        (wallboardData.get('queueKeys')?.size || 0)
                    }
                />
            ) : null}
        </React.Fragment>
    );
};

export default WallboardHuntGroupsMultiple;

interface CustomField {
    key: string;
    value: string;
    description: string;
    selected: boolean;
}
